export type Identified = { id: string };

export type MissionRunTimeline<TEvent> = {
    events: TEvent[];
    durationMs: number;
};

type Equality<T> = (previous: T, next: T) => boolean;
type Reconcile<T> = (previous: T, next: T) => T;

/**
 * Reconcile an ordered entity collection without replacing unchanged objects.
 *
 * The returned array is also reused when both its order and every entity
 * reference are unchanged. This makes it safe to use as React state: a
 * background refresh that found no changes causes no downstream work.
 */
export const reconcileEntities = <T extends Identified>(
    previous: T[],
    incoming: T[],
    equal: Equality<T>,
    reconcile: Reconcile<T> = (previous, next) => equal(previous, next) ? previous : next,
): T[] => {
    const previousById = new Map(previous.map((entity) => [entity.id, entity]));
    let changed = previous.length !== incoming.length;
    const reconciled = incoming.map((entity) => {
        const oldEntity = previousById.get(entity.id);
        if (oldEntity) {
            const reconciledEntity = reconcile(oldEntity, entity);
            if (reconciledEntity !== oldEntity) changed = true;
            return reconciledEntity;
        }
        changed = true;
        return entity;
    });

    if (!changed && reconciled.every((entity, index) => entity === previous[index])) {
        return previous;
    }
    return reconciled;
};

/**
 * Small in-memory repository for the run page.
 *
 * Fetching remains the responsibility of the existing direct GraphQL and fleet
 * REST adapters. This class owns only identity, freshness, and structural
 * sharing so those transports can be changed independently of the view.
 */
export class MissionRunRepository<TRun extends Identified, TEvent extends Identified> {
    private runs: TRun[] = [];

    private readonly timelines = new Map<string, MissionRunTimeline<TEvent>>();

    private readonly staleTimelines = new Set<string>();

    private readonly recordings = new Map<string, unknown>();

    getRuns(): TRun[] {
        return this.runs;
    }

    reconcileRuns(incoming: TRun[], equal: Equality<TRun>): TRun[] {
        const previousById = new Map(this.runs.map((run) => [run.id, run]));
        this.runs = reconcileEntities(this.runs, incoming, equal);
        for (const run of incoming) {
            const previous = previousById.get(run.id);
            if (previous && !equal(previous, run)) this.staleTimelines.add(run.id);
        }
        const incomingIds = new Set(incoming.map((run) => run.id));
        for (const previous of previousById.values()) {
            if (!incomingIds.has(previous.id)) this.deleteRun(previous.id);
        }
        return this.runs;
    }

    getTimeline(runId: string): MissionRunTimeline<TEvent> | undefined {
        return this.timelines.get(runId);
    }

    hasRecording(runId: string): boolean {
        return this.recordings.has(runId);
    }

    getRecording<TRecording>(runId: string): TRecording | null | undefined {
        return this.recordings.get(runId) as TRecording | null | undefined;
    }

    reconcileRecording<TRecording>(
        runId: string,
        incoming: TRecording | null,
        equal: Equality<TRecording | null>,
    ): TRecording | null {
        const previous = this.recordings.get(runId) as TRecording | null | undefined;
        const reconciled = this.recordings.has(runId) && equal(previous ?? null, incoming)
            ? previous ?? null
            : incoming;
        this.recordings.set(runId, reconciled);
        return reconciled;
    }

    isTimelineStale(runId: string): boolean {
        return this.staleTimelines.has(runId);
    }

    markTimelineStale(runId?: string): void {
        if (runId) {
            this.staleTimelines.add(runId);
            return;
        }
        for (const key of this.timelines.keys()) this.staleTimelines.add(key);
    }

    reconcileTimeline(
        runId: string,
        incoming: MissionRunTimeline<TEvent>,
        equal: Equality<TEvent>,
        reconcile: Reconcile<TEvent> = (previous, _next) => previous,
    ): MissionRunTimeline<TEvent> {
        const previous = this.timelines.get(runId);
        if (!previous) {
            this.timelines.set(runId, incoming);
        } else {
            const events = reconcileEntities(previous.events, incoming.events, equal, reconcile);
            this.timelines.set(runId, events === previous.events && incoming.durationMs === previous.durationMs
                ? previous
                : { events, durationMs: incoming.durationMs });
        }
        this.staleTimelines.delete(runId);
        return this.timelines.get(runId) as MissionRunTimeline<TEvent>;
    }

    deleteRun(runId: string): void {
        this.timelines.delete(runId);
        this.recordings.delete(runId);
        this.staleTimelines.delete(runId);
    }
}
